/**
 * ArticleReviewerProgressReport — Matrix-style progress report showing
 * articles as rows and reviewers as columns with status indicators.
 *
 * Tasks: 4.1–4.7
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { PRIMARY } from '../legacy-ball/table-management/designTokens';
import {
  ReviewProgressArticle,
  buildReviewerColumns,
  computeCellStatus,
  computeSummaryStats,
  computeTotalComments,
  resolveSectionLabel,
  applyFilter,
  sortByCompletion,
  formatTooltip,
  BOOKLET_SECTIONS,
} from './progressReportUtils';
import ArticleDetailDrilldown from './ArticleDetailDrilldown';

// ── Status colors for submission status badges (matching WeekendBookletPage) ──

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Draft: { bg: '#f3f4f6', text: '#6b7280' },
  Submitted: { bg: '#dbeafe', text: '#1d4ed8' },
  UnderReview: { bg: '#fef3c7', text: '#d97706' },
  RevisionRequested: { bg: '#fee2e2', text: '#dc2626' },
  Approved: { bg: '#dcfce7', text: '#16a34a' },
  ReadyForLayout: { bg: '#f3e8ff', text: '#9333ea' },
  Published: { bg: '#d1fae5', text: '#065f46' },
};

/** Format camelCase status to separate words (e.g., "UnderReview" → "Under Review") */
function formatStatusLabel(status: string): string {
  return status.replace(/([a-z])([A-Z])/g, '$1 $2');
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    padding: '1.5rem',
    maxWidth: '100%',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  } as React.CSSProperties,
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1a2e23',
    margin: 0,
  } as React.CSSProperties,
  printBtn: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '0.5rem 1.25rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
  summaryRow: {
    display: 'flex',
    gap: '2rem',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  statItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
  } as React.CSSProperties,
  statLabel: {
    fontSize: '0.75rem',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  } as React.CSSProperties,
  statValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a2e23',
  } as React.CSSProperties,
  allComplete: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#16a34a',
  } as React.CSSProperties,
  filterRow: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  select: {
    padding: '0.4rem 0.75rem',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '0.875rem',
    color: '#374151',
    backgroundColor: '#fff',
  } as React.CSSProperties,
  scrollContainer: {
    overflowX: 'auto' as const,
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8125rem',
    minWidth: '600px',
  } as React.CSSProperties,
  thArticle: {
    position: 'sticky' as const,
    left: 0,
    backgroundColor: '#f9fafb',
    padding: '0.5rem 0.75rem',
    textAlign: 'left' as const,
    fontWeight: 600,
    borderBottom: '2px solid #e5e7eb',
    minWidth: '200px',
    maxWidth: '200px',
    zIndex: 2,
  } as React.CSSProperties,
  thReviewer: {
    padding: '0.5rem 0.5rem',
    textAlign: 'center' as const,
    fontWeight: 600,
    borderBottom: '2px solid #e5e7eb',
    whiteSpace: 'nowrap' as const,
    backgroundColor: '#f9fafb',
    fontSize: '0.75rem',
  } as React.CSSProperties,
  tdArticle: {
    position: 'sticky' as const,
    left: 0,
    backgroundColor: '#fff',
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid #f3f4f6',
    minWidth: '200px',
    maxWidth: '200px',
    zIndex: 1,
  } as React.CSSProperties,
  articleTitle: {
    maxWidth: '200px',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
    fontWeight: 500,
    color: '#1f2937',
    margin: 0,
  } as React.CSSProperties,
  articleSection: {
    fontSize: '0.7rem',
    color: '#6b7280',
    marginTop: '2px',
  } as React.CSSProperties,
  statusBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '0.65rem',
    fontWeight: 600,
    marginTop: '2px',
  } as React.CSSProperties,
  tdCell: {
    padding: '0.4rem 0.5rem',
    textAlign: 'center' as const,
    borderBottom: '1px solid #f3f4f6',
    cursor: 'default',
  } as React.CSSProperties,
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
    color: '#6b7280',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  errorBanner: {
    padding: '1rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#b91c1c',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as React.CSSProperties,
  retryBtn: {
    backgroundColor: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '0.35rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.8rem',
  } as React.CSSProperties,
  emptyState: {
    padding: '3rem',
    textAlign: 'center' as const,
    color: '#6b7280',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  commentBadge: {
    fontSize: '0.75rem',
    color: '#6b7280',
    fontWeight: 400,
    marginLeft: '1px',
  } as React.CSSProperties,
  popupWarning: {
    marginTop: '0.75rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '6px',
    color: '#92400e',
    fontSize: '0.8rem',
  } as React.CSSProperties,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function ArticleReviewerProgressReport() {
  const [articles, setArticles] = useState<ReviewProgressArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'title' | 'completion'>('title');
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [drilldownIndex, setDrilldownIndex] = useState<number | null>(null);

  // ── Data Fetching ─────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<ReviewProgressArticle[]>('/weekend-booklet/review-progress');
      setArticles(data);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? String((err as { message: string }).message)
        : 'Failed to load review progress';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Derived Data ──────────────────────────────────────────────

  const filteredArticles = useMemo(() => {
    let result = applyFilter(articles, sectionFilter);
    if (sortBy === 'completion') {
      result = sortByCompletion(result);
    }
    // Default 'title' sort is already provided by API ordering
    return result;
  }, [articles, sectionFilter, sortBy]);

  // Close drilldown if the current index is out of bounds after filtering changes
  useEffect(() => {
    if (drilldownIndex !== null && drilldownIndex >= filteredArticles.length) {
      setDrilldownIndex(null);
    }
  }, [filteredArticles, drilldownIndex]);

  const reviewerColumns = useMemo(() => buildReviewerColumns(articles), [articles]);
  const stats = useMemo(() => computeSummaryStats(filteredArticles), [filteredArticles]);
  const totalComments = useMemo(
    () => filteredArticles.reduce((sum, article) => sum + computeTotalComments(article), 0),
    [filteredArticles],
  );

  // ── Print Handler ─────────────────────────────────────────────

  const handlePrint = useCallback(() => {
    setPopupBlocked(false);

    // Build reviewer completion counts
    const reviewerCounts = reviewerColumns.map((reviewer) => {
      let completed = 0;
      let total = 0;
      for (const article of filteredArticles) {
        const assignment = article.reviewers.find((r) => r.memberId === reviewer.memberId);
        if (assignment) {
          total++;
          if (assignment.reviewed) completed++;
        }
      }
      return { name: reviewer.fullName, completed, total };
    });

    const reviewerCountsHtml = reviewerCounts
      .map((r) => `<li>${r.name}: ${r.completed} of ${r.total}</li>`)
      .join('');

    // Build table header
    const headerCells =
      `<th style="text-align:left;padding:6px 8px;border:1px solid #ccc;font-size:11px;">Article</th>` +
      reviewerColumns
        .map((r) => `<th style="text-align:center;padding:6px 4px;border:1px solid #ccc;font-size:11px;">${r.displayName}</th>`)
        .join('');

    // Build table rows
    const rows = filteredArticles
      .map((article) => {
        const cells = reviewerColumns
          .map((reviewer) => {
            const cellStatus = computeCellStatus(article, reviewer.memberId);
            const assignment = article.reviewers.find((r) => r.memberId === reviewer.memberId);
            const commentCount = assignment?.commentCount ?? 0;
            const commentSuffix = commentCount > 0 ? ` (${commentCount})` : '';
            if (cellStatus.type === 'completed') return `<td style="text-align:center;border:1px solid #ccc;color:#16a34a;font-size:12px;">✓${commentSuffix}</td>`;
            if (cellStatus.type === 'pending') return `<td style="text-align:center;border:1px solid #ccc;color:#d97706;font-size:12px;">⏱${commentSuffix}</td>`;
            return `<td style="text-align:center;border:1px solid #ccc;color:#9ca3af;font-size:12px;">—</td>`;
          })
          .join('');
        return `<tr><td style="padding:6px 8px;border:1px solid #ccc;font-size:12px;">${article.title}</td>${cells}</tr>`;
      })
      .join('');

    const html = `<!DOCTYPE html><html><head>
      <title>Article Reviewer Progress Report</title>
      <style>
        body { font-family: sans-serif; padding: 20px; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        p { color: #666; font-size: 12px; margin: 4px 0; }
        ul { padding-left: 20px; font-size: 12px; }
        @media print { body { padding: 10px; } }
      </style>
    </head><body>
      <h1>Article Reviewer Progress Report</h1>
      <p>Generated: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      <p>Total Articles: ${stats.totalArticles} | Completion: ${stats.percentage}%</p>
      <h3 style="font-size:13px;margin-top:12px;">Per-Reviewer Completion</h3>
      <ul>${reviewerCountsHtml}</ul>
      <table><thead><tr>${headerCells}</tr></thead><tbody>${rows}</tbody></table>
    </body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    } else {
      setPopupBlocked(true);
    }
  }, [filteredArticles, reviewerColumns, stats]);

  // ── Render: Loading ───────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.spinner}>Loading review progress…</div>
      </div>
    );
  }

  // ── Render: Error ─────────────────────────────────────────────

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBanner}>
          <span>{error}</span>
          <button style={styles.retryBtn} onClick={fetchData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Render: Empty State ───────────────────────────────────────

  if (articles.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>No articles have reviewer assignments yet</div>
      </div>
    );
  }

  // ── Render: Main Report ───────────────────────────────────────

  return (
    <div style={styles.container}>
      {/* Report Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>Article Reviewer Progress Report</h2>
        <button style={styles.printBtn} onClick={handlePrint}>
          Print
        </button>
      </div>

      {/* Popup blocked warning */}
      {popupBlocked && (
        <div style={styles.popupWarning}>
          Popup was blocked. Please allow popups for this site to use the print feature.
        </div>
      )}

      {/* Summary Stats */}
      <div style={styles.summaryRow}>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>Articles</span>
          <span style={styles.statValue}>{stats.totalArticles}</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>Reviews</span>
          <span style={styles.statValue}>
            {stats.completedReviews} of {stats.totalAssignments}
          </span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>Completion</span>
          <span style={styles.statValue}>{stats.percentage}%</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>Comments</span>
          <span style={styles.statValue}>{totalComments}</span>
        </div>
        {stats.percentage === 100 && stats.totalAssignments > 0 && (
          <span style={styles.allComplete}>All reviews complete ✓</span>
        )}
      </div>

      {/* Filter Controls */}
      <div style={styles.filterRow}>
        <select
          style={styles.select}
          value={sectionFilter}
          onChange={(e) => setSectionFilter(e.target.value)}
          aria-label="Filter by section"
        >
          <option value="all">All Sections</option>
          {BOOKLET_SECTIONS.map((section) => (
            <option key={section} value={section}>
              {section}
            </option>
          ))}
        </select>

        <select
          style={styles.select}
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'title' | 'completion')}
          aria-label="Sort articles"
        >
          <option value="title">Title (A–Z)</option>
          <option value="completion">Completion (lowest first)</option>
        </select>
      </div>

      {/* Empty filter result */}
      {filteredArticles.length === 0 && (
        <div style={styles.emptyState}>No articles found for the selected section</div>
      )}

      {/* Matrix Grid */}
      {filteredArticles.length > 0 && (
        <div style={styles.scrollContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.thArticle}>Article</th>
                {reviewerColumns.map((reviewer) => (
                  <th key={reviewer.memberId} style={styles.thReviewer} title={reviewer.fullName}>
                    {reviewer.displayName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredArticles.map((article, idx) => {
                const sectionLabel = resolveSectionLabel(article.bookletSection);
                const statusColor = STATUS_COLORS[article.status] || { bg: '#f3f4f6', text: '#6b7280' };

                return (
                  <tr
                    key={article.id}
                    onClick={() => setDrilldownIndex(idx)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={styles.tdArticle}>
                      <div style={styles.articleTitle} title={article.title}>
                        {article.title}
                      </div>
                      <div style={styles.articleSection}>{sectionLabel}</div>
                      <span
                        style={{
                          ...styles.statusBadge,
                          backgroundColor: statusColor.bg,
                          color: statusColor.text,
                        }}
                      >
                        {formatStatusLabel(article.status)}
                      </span>
                    </td>
                    {reviewerColumns.map((reviewer) => {
                      const cellStatus = computeCellStatus(article, reviewer.memberId);
                      const assignment = article.reviewers.find((r) => r.memberId === reviewer.memberId);
                      const commentCount = assignment?.commentCount ?? 0;

                      if (cellStatus.type === 'completed') {
                        const tooltip = formatTooltip({
                          reviewed: true,
                          reviewedAt: cellStatus.reviewedAt,
                          assignedAt: '',
                        });
                        return (
                          <td
                            key={reviewer.memberId}
                            style={styles.tdCell}
                            title={tooltip}
                            tabIndex={0}
                            aria-label={tooltip}
                          >
                            <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
                            {commentCount > 0 && (
                              <span style={styles.commentBadge}>({commentCount})</span>
                            )}
                          </td>
                        );
                      }

                      if (cellStatus.type === 'pending') {
                        const tooltip = formatTooltip({
                          reviewed: false,
                          reviewedAt: null,
                          assignedAt: cellStatus.assignedAt,
                        });
                        return (
                          <td
                            key={reviewer.memberId}
                            style={styles.tdCell}
                            title={tooltip}
                            tabIndex={0}
                            aria-label={tooltip}
                          >
                            <span style={{ color: '#d97706', fontWeight: 700 }}>⏱</span>
                            {commentCount > 0 && (
                              <span style={styles.commentBadge}>({commentCount})</span>
                            )}
                          </td>
                        );
                      }

                      // No assignment
                      return (
                        <td key={reviewer.memberId} style={styles.tdCell}>
                          <span style={{ color: '#9ca3af' }}>—</span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Article Detail Drilldown */}
      {drilldownIndex !== null && filteredArticles[drilldownIndex] && (
        <ArticleDetailDrilldown
          article={filteredArticles[drilldownIndex]}
          articles={filteredArticles}
          currentIndex={drilldownIndex}
          onClose={() => setDrilldownIndex(null)}
          onNavigate={(idx) => setDrilldownIndex(idx)}
        />
      )}
    </div>
  );
}
